import React, { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';

import { TABLE_WATERMARK_ENABLED } from 'constants/tableWatermark';
import { useTableWatermarkGuard } from 'hooks';
import {
  TABLE_WATERMARK_ATTR,
  TABLE_WATERMARK_DEFAULT_LEFT,
  TABLE_WATERMARK_LAYER_ATTR,
  TABLE_WATERMARK_OFFSET_ATTR,
  getWatermarkLayerLayoutStyle,
} from 'styles/tableWatermark';

export const WatermarkedTableContainer = forwardRef(
  function WatermarkedTableContainer(
    {
      className,
      children,
      watermarkOffsetTop = 8,
      watermarkLeft = TABLE_WATERMARK_DEFAULT_LEFT,
      watermarkLeftMobile,
      ...props
    },
    ref
  ) {
    const innerRef = useRef(null);
    const resolvedLeftMobile = watermarkLeftMobile || watermarkLeft;

    useTableWatermarkGuard(innerRef, {
      offsetTop: watermarkOffsetTop,
      left: watermarkLeft,
      leftMobile: resolvedLeftMobile,
    });

    const setRef = node => {
      innerRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
        return;
      }

      if (ref) {
        // eslint-disable-next-line no-param-reassign
        ref.current = node;
      }
    };

    return (
      <div
        ref={setRef}
        className={className}
        {...props}
        {...(TABLE_WATERMARK_ENABLED
          ? {
              [TABLE_WATERMARK_ATTR]: 'true',
              [TABLE_WATERMARK_OFFSET_ATTR]: watermarkOffsetTop,
            }
          : {})}
      >
        {children}
        {TABLE_WATERMARK_ENABLED && (
          <div
            {...{ [TABLE_WATERMARK_LAYER_ATTR]: 'true' }}
            aria-hidden="true"
            style={getWatermarkLayerLayoutStyle(watermarkOffsetTop)}
          />
        )}
      </div>
    );
  }
);

WatermarkedTableContainer.displayName = 'WatermarkedTableContainer';

WatermarkedTableContainer.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  watermarkOffsetTop: PropTypes.number,
  watermarkLeft: PropTypes.string,
  watermarkLeftMobile: PropTypes.string,
};

WatermarkedTableContainer.defaultProps = {
  className: null,
  children: null,
  watermarkOffsetTop: 8,
  watermarkLeft: TABLE_WATERMARK_DEFAULT_LEFT,
  watermarkLeftMobile: null,
};
